import React, { useMemo, useState } from 'react';
import {
  Typography,
  IconButton,
  Toolbar,
  Box,
  Tooltip,
  Drawer,
  List,
  Badge,
  Card,
  Divider,
  FormControlLabel,
  Switch,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  DarkModeOutlined,
  LightModeOutlined,
  InfoOutlined,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import CircleIcon from '@mui/icons-material/Circle';
import { clearAllNotifications } from '../../store/notification/slice';
import { clearAllOperations } from '../../store/bulk/operations/slice';
import Wheel2Logo from '../../assets/Images/Wheel2Logo.png';
import Wheel2LogoDark from '../../assets/Images/Wheel2Logo-dark.png';
import {
  AppBar,
  HeaderContainer,
  HeaderWrapper,
  StatusContainer,
  StatusDot,
  StatusText,
  CustomCardContent,
} from './Style/Header.style';
import {
  HeaderProps,
  Notification,
  NotificationsState,
} from '../../helpers/types';
import { formatedDate, removeExpiredNotifications } from '../../utils';
import Button from '../Button/Button';
import { useAuth0 } from '../../__mocks__/@auth0/auth0-react';
import { useThemeMode } from '../../context/ThemeMode/ThemeModeContext';
import {
  useWebSocket,
  WebSocketStatus,
} from '../../context/WebSocket/WebSocketContext';
import { openCommandPalette } from '../CommandPalette/commandPaletteBus';
import HeaderClock from './HeaderClock';
import { useCurrentUserQuery } from '../../queries/user';

const CONNECTION_META: Record<
  WebSocketStatus,
  { label: string; tooltip: string }
> = {
  connected: {
    label: 'Connected',
    tooltip:
      'Server connection established. Live data is streaming and threats on the screen are updated in real time.',
  },
  reconnecting: {
    label: 'Reconnecting…',
    tooltip:
      'Connection to the server was lost. Trying to reconnect — live updates are paused.',
  },
  disconnected: {
    label: 'Disconnected',
    tooltip:
      'No server connection. Live updates are not streaming — data on screen may be stale.',
  },
};

const formatUtcTime = (timestamp: number): string =>
  `${new Date(timestamp).toISOString().slice(11, 19)} UTC`;

const buildThreatLink = (notification: Notification): string | null => {
  const { protected_asset_id, threat_id, company_id } = notification;
  if (protected_asset_id && threat_id && company_id) {
    return `/threats/${protected_asset_id}/${threat_id}/${company_id}`;
  }
  return null;
};

const Header = ({ open }: HeaderProps) => {
  const [notificationsDrawerOpen, setNotificationsDrawerOpen] = useState(false);
  const [pendingOperationsDrawerOpen, setPendingOperationsDrawerOpen] =
    useState(false);
  const notifications = useSelector(
    (state: { notification: NotificationsState }) => state.notification.data,
  );
  const pendingOperations = useSelector(
    (state: any) => state.bulkOperations.pendingOperations,
  );
  const pendingCount = Object.keys(pendingOperations).length;
  const { user } = useAuth0();
  const { data: currentUser } = useCurrentUserQuery();
  const userData = { data: currentUser };
  const theme = useTheme();
  const { mode, toggleMode } = useThemeMode();
  const { status: connectionStatus, lastConnectedAt } = useWebSocket();

  const connectionTooltip = useMemo(() => {
    const base = CONNECTION_META[connectionStatus].tooltip;
    if (connectionStatus === 'connected' && lastConnectedAt) {
      return `${base} Connected since ${formatUtcTime(lastConnectedAt)}.`;
    }
    if (lastConnectedAt) {
      return `${base} Last connected at ${formatUtcTime(lastConnectedAt)}.`;
    }
    return `${base} No connection has been established in this session.`;
  }, [connectionStatus, lastConnectedAt]);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [myNotificationsOnly, setMyNotificationsOnly] = useState(false);

  const notificationFilterData = useMemo(() => {
    let filtered = removeExpiredNotifications(notifications);
    if (statusFilter) {
      filtered = filtered.filter(
        (n: Notification) => n.status === statusFilter,
      );
    }
    if (myNotificationsOnly && user?.email) {
      filtered = filtered.filter(
        (n: Notification) => n.userEmail === user.email,
      );
    }
    return filtered;
  }, [notifications, statusFilter, myNotificationsOnly, user?.email]);

  const nonExpiredNotifications = useMemo(
    () => removeExpiredNotifications(notifications),
    [notifications],
  );

  const availableStatuses = useMemo(() => {
    const statuses = new Set(
      nonExpiredNotifications.map((n: Notification) => n.status),
    );
    return Array.from(statuses).sort();
  }, [nonExpiredNotifications]);

  const toggleNotificationsDrawer = (open: boolean) =>
    setNotificationsDrawerOpen(open);
  const dispatch = useDispatch();
  const togglePendingOperationsDrawer = (open: boolean) =>
    setPendingOperationsDrawerOpen(open);

  const renderNotifications = () => {
    return (
      <List>
        {notificationFilterData?.map(
          (notification: Notification, index: number) => {
            const {
              thread_name,
              status,
              userEmail,
              date,
              errorMessage,
              isError,
              operation,
            } = notification;
            const threatLink = buildThreatLink(notification);
            const threatNameElement = threatLink ? (
              <a
                href={threatLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                {thread_name}
              </a>
            ) : (
              thread_name
            );
            const takedownLinkElement = notification.takedown_id ? (
              <>
                {' '}
                <a
                  href={`/takedown/${notification.takedown_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  View takedown
                </a>
              </>
            ) : null;
            const isTakedownOperation = operation === 'bulk_takedown';
            const isStatusUpdateOperation =
              operation === 'takedown_status_update';
            return (
              <React.Fragment key={index}>
                <Card
                  sx={{
                    padding: 0,
                    border: 0,
                  }}
                >
                  <CustomCardContent>
                    <Typography
                      variant="body2"
                      color={isError ? 'error.main' : 'text.primary'}
                    >
                      {isError ? (
                        <>
                          <strong>
                            {isTakedownOperation
                              ? 'Takedown failed for '
                              : isStatusUpdateOperation
                              ? 'Takedown status update failed for '
                              : 'Error with '}
                            {threatNameElement}
                            {isTakedownOperation || isStatusUpdateOperation
                              ? ':'
                              : ' operation:'}
                          </strong>{' '}
                          {errorMessage}
                          {takedownLinkElement}
                        </>
                      ) : (
                        <>
                          {isTakedownOperation ? (
                            <>
                              Takedown created for{' '}
                              <strong>{threatNameElement}</strong>.
                              {takedownLinkElement}
                            </>
                          ) : isStatusUpdateOperation ? (
                            <>
                              Takedown for <strong>{threatNameElement}</strong>{' '}
                              updated to <strong>{status}</strong>.
                              {takedownLinkElement}
                            </>
                          ) : operation === 'created' ? (
                            <>
                              <strong>{threatNameElement}</strong> created as{' '}
                              <strong>{status}</strong>
                            </>
                          ) : (
                            <>
                              <strong>{threatNameElement}</strong> changed
                              status to <strong>{status}</strong>
                            </>
                          )}
                        </>
                      )}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        by {userEmail}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatedDate(new Date(date))} UTC
                      </Typography>
                    </Box>
                  </CustomCardContent>
                </Card>
                <Divider />
              </React.Fragment>
            );
          },
        )}

        {notificationFilterData?.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              No data found
            </Typography>
          </Box>
        )}
      </List>
    );
  };

  return (
    <AppBar position="fixed" open={open}>
      <Toolbar>
        <HeaderContainer>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <img
              src={mode === 'dark' ? Wheel2LogoDark : Wheel2Logo}
              alt="wheel2"
              style={{ height: 30 }}
            />
            <Box
              sx={{ display: 'flex', alignItems: 'center' }}
              data-testid="header-status-widget"
            >
              {/* Real WebSocket status (WHEEL-320) in the logo widget — the
                  static master chip it replaces always said "Connected". */}
              <Tooltip title={connectionTooltip}>
                <StatusContainer
                  aria-label={connectionTooltip}
                  data-testid="header-connection-status"
                  sx={{ border: 'none', padding: 0, marginLeft: 0 }}
                >
                  <StatusDot status={connectionStatus} />
                  <StatusText>
                    {CONNECTION_META[connectionStatus].label}
                  </StatusText>
                </StatusContainer>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ mx: 1.5 }} />
              <HeaderClock />
            </Box>
            {pendingCount > 0 && (
              <Tooltip title="Click to see pending operations">
                <StatusContainer
                  onClick={() => togglePendingOperationsDrawer(true)}
                  sx={{ cursor: 'pointer' }}
                >
                  <CircleIcon
                    sx={{
                      color: 'primary.main',
                      width: '8px',
                      height: '8px',
                      marginRight: '4px',
                    }}
                  />
                  <StatusText>{pendingCount} pending</StatusText>
                </StatusContainer>
              </Tooltip>
            )}
          </Box>
          <Box sx={HeaderWrapper}>
            <Typography sx={{ ml: 2, fontSize: '14px' }}>
              {user?.name}
            </Typography>
            <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Command palette (Ctrl/⌘ + K)">
                <IconButton
                  size="small"
                  onClick={openCommandPalette}
                  color="inherit"
                  data-testid="header-command-palette-open"
                >
                  <SearchIcon fontSize="small" />
                  <Typography
                    variant="caption"
                    sx={{
                      ml: 0.5,
                      px: 0.5,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: '4px',
                      color: 'text.secondary',
                      fontSize: 11,
                      lineHeight: 1.4,
                    }}
                  >
                    ⌘K
                  </Typography>
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  mode === 'dark'
                    ? 'Switch to light mode'
                    : 'Switch to dark mode'
                }
              >
                <IconButton
                  size="small"
                  onClick={toggleMode}
                  color="inherit"
                  data-testid="theme-toggle-button"
                >
                  {mode === 'dark' ? (
                    <LightModeOutlined fontSize="small" />
                  ) : (
                    <DarkModeOutlined fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <IconButton
                size="large"
                data-testid="notifications-button"
                onClick={() => toggleNotificationsDrawer(true)}
                color="inherit"
              >
                <Badge
                  badgeContent={nonExpiredNotifications?.length}
                  color="error"
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Box>
          </Box>
        </HeaderContainer>
      </Toolbar>
      <Drawer
        anchor="right"
        open={notificationsDrawerOpen}
        onClose={() => toggleNotificationsDrawer(false)}
        variant="temporary"
        sx={{ zIndex: 1300 }}
        PaperProps={{
          sx: { boxShadow: 3 },
        }}
      >
        <Box
          sx={{
            padding: '6px 8px',
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
            }}
          >
            Latest threat status updates
            <Tooltip title="Shows real-time threat status updates received via WebSocket. Notifications are stored for 7 days.">
              <InfoOutlined
                sx={{
                  fontSize: 16,
                  ml: 0.5,
                  color: 'text.secondary',
                  cursor: 'help',
                }}
              />
            </Tooltip>
          </Typography>
          {notificationFilterData?.length > 0 && (
            <Button
              variant="text"
              size="small"
              onClick={() => dispatch(clearAllNotifications())}
              color="primary"
            >
              Clear All
            </Button>
          )}
        </Box>
        <Box
          sx={{
            padding: '10px 12px',
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          <Autocomplete
            size="small"
            options={availableStatuses}
            value={statusFilter}
            onChange={(_, value) => setStatusFilter(value)}
            renderInput={params => (
              <TextField {...params} placeholder="Filter by status" />
            )}
            sx={{ minWidth: 200 }}
            data-testid="notification-status-filter"
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={myNotificationsOnly}
                onChange={e => setMyNotificationsOnly(e.target.checked)}
                data-testid="my-notifications-toggle"
              />
            }
            label={<Typography variant="caption">My notifications</Typography>}
            sx={{ ml: 0, whiteSpace: 'nowrap' }}
          />
        </Box>
        <Box
          sx={{ width: 680, padding: '0 12px', overflowY: 'auto', flex: 1 }}
          role="presentation"
        >
          {renderNotifications()}
        </Box>
      </Drawer>

      <Drawer
        anchor="right"
        open={pendingOperationsDrawerOpen}
        onClose={() => togglePendingOperationsDrawer(false)}
        variant="temporary"
        sx={{ zIndex: 1300 }}
        PaperProps={{
          sx: { boxShadow: 3 },
        }}
      >
        <Box
          sx={{
            padding: '10px 15px',
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
            }}
            gutterBottom
          >
            Pending Operations
          </Typography>
          {Object.keys(pendingOperations).length > 0 && (
            <Tooltip title="Clearing pending operations will clean up the list and stop any further retries. Operations queued in the server could still get through.">
              <Button
                variant="text"
                size="small"
                onClick={() => dispatch(clearAllOperations())}
                color="primary"
              >
                Clear All
              </Button>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ width: 400, padding: '0 12px' }} role="presentation">
          <List>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Failed Operations (
                  {
                    Object.values(pendingOperations).filter(
                      (op: any) => op.hasError,
                    ).length
                  }
                  )
                </Typography>
              </Box>
              {Object.values(pendingOperations)
                .filter((op: any) => op.hasError)
                .map((operation: any, index: number) => (
                  <Card
                    variant="outlined"
                    key={index}
                    sx={{
                      marginBottom: 1,
                      marginTop: 1,
                      padding: 0,
                      borderColor: theme.palette.error.main,
                      backgroundColor: alpha(theme.palette.error.main, 0.05),
                    }}
                  >
                    <CustomCardContent>
                      <Box>
                        <Typography variant="body2" color="error">
                          <strong>{operation.threatName}</strong>
                        </Typography>
                        <Typography
                          variant="body2"
                          color="error.light"
                          sx={{ mt: 0.5 }}
                        >
                          {operation.errorMessage}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mt: 1,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Started at{' '}
                          {formatedDate(new Date(operation.startedAt))}
                        </Typography>
                      </Box>
                    </CustomCardContent>
                  </Card>
                ))}
            </Box>

            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              In Progress (
              {
                Object.values(pendingOperations).filter(
                  (op: any) => !op.hasError,
                ).length
              }
              )
            </Typography>
            {Object.values(pendingOperations)
              .filter((op: any) => !op.hasError)
              .map((operation: any, index: number) => (
                <Card
                  variant="outlined"
                  key={index}
                  sx={{
                    marginBottom: 1,
                    marginTop: 1,
                    padding: 0,
                    borderColor: theme.palette.info.main,
                    backgroundColor: alpha(theme.palette.info.main, 0.05),
                  }}
                >
                  <CustomCardContent>
                    <Box>
                      <Typography variant="body2" color="primary">
                        <strong>{operation.threatName}</strong>
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        Changing status to{' '}
                        <strong>{operation.expectedStatus}</strong>
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Started at{' '}
                        {new Date(operation.startedAt).toLocaleString()}
                      </Typography>
                    </Box>
                  </CustomCardContent>
                </Card>
              ))}

            {Object.keys(pendingOperations).length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  No pending operations
                </Typography>
              </Box>
            )}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Header;
